import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Building2, CheckCircle2, ShieldCheck, FileText, ChevronRight, Home } from 'lucide-react';
import { MobileLayout } from '../../components/MobileLayout';
import { useScreenInit } from '../../useScreenInit.js';
import { getReferenteById, referenteFullName, TYPE_STYLE } from './referentiData';
import { APARTMENTS, TYPE_STYLE as APT_TYPE_STYLE } from './appartamentiData';
export function CmReferenteDettaglioPage() {
  useScreenInit();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const referente = getReferenteById(id);
  if (!referente) {
    return <MobileLayout data-id="element-2284">
        <div className="text-center py-20" data-id="element-2285">
          <p className="text-sm text-muted-foreground mb-4" data-id="element-2286">
            Referente non trovato.
          </p>
          <Link to="/cm/clienti/referenti" className="inline-flex items-center gap-1.5 text-sm font-medium text-coral hover:underline" data-id="element-2287">
            <ArrowLeft size={15} data-id="element-2288" />
            Torna ai referenti
          </Link>
        </div>
      </MobileLayout>;
  }
  const fullName = referenteFullName(referente);
  // Associated apartments: matched by the apartment's "referente" field.
  const apartments = APARTMENTS.filter(a => a.referente === fullName);
  const activeContracts = apartments.filter(a => a.contract?.active).length;
  return <MobileLayout data-id="element-2289">
      {/* Back */}
      <Link to="/cm/clienti/referenti" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-navy transition-colors mb-4" data-id="element-2290">
        <ArrowLeft size={16} data-id="element-2291" />
        Referenti
      </Link>

      {/* Header card */}
      <div className="bg-white border border-border rounded-xl shadow-sm p-4 lg:p-5 mb-6" data-id="element-2292">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4" data-id="element-2293">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-navy font-bold flex-shrink-0 text-xl" data-id="element-2294">
            {referente.firstName[0]}
            {referente.lastName[0]}
          </div>
          <div className="flex-1 min-w-0" data-id="element-2295">
            <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-2296">
              <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-2297">
                {fullName}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${TYPE_STYLE[referente.type]}`} data-id="element-2298">
                {referente.type}
              </span>
              {referente.hasVikey && <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-navy/5 text-navy border border-navy/20" data-id="element-2299">
                  <ShieldCheck size={12} data-id="element-2300" />
                  Vikey
                </span>}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-id="element-2301">
              <MapPin size={14} className="flex-shrink-0" data-id="element-2302" />
              {referente.zone}
            </div>
            {/* Contacts */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3" data-id="element-2303">
              <a href={`mailto:${referente.email}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-navy transition-colors min-w-0" data-id="element-2304">
                <Mail size={15} className="text-gray-400 flex-shrink-0" data-id="element-2305" />
                <span className="truncate" data-id="element-2306">{referente.email}</span>
              </a>
              <a href={`tel:${referente.phone}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-navy transition-colors" data-id="element-2307">
                <Phone size={15} className="text-gray-400 flex-shrink-0" data-id="element-2308" />
                {referente.phone}
              </a>
            </div>
          </div>
          <div className="flex-shrink-0" data-id="element-2309">
            {referente.activeContract ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-2310">
                <CheckCircle2 size={13} data-id="element-2311" />
                Contratto attivo
              </span> : <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200" data-id="element-2312">
                Nessun contratto
              </span>}
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-2313">
        <div className="bg-white border border-border rounded-xl p-4" data-id="element-2314">
          <div className="w-9 h-9 rounded-lg bg-navy/10 text-navy flex items-center justify-center mb-3" data-id="element-2315">
            <Building2 size={18} data-id="element-2316" />
          </div>
          <div className="text-2xl font-bold text-navy leading-tight" data-id="element-2317">
            {apartments.length}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2318">
            Appartamenti associati
          </div>
        </div>
        <div className="bg-white border border-border rounded-xl p-4" data-id="element-2319">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3" data-id="element-2320">
            <FileText size={18} data-id="element-2321" />
          </div>
          <div className="text-2xl font-bold text-navy leading-tight" data-id="element-2322">
            {activeContracts}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2323">
            Contratti attivi
          </div>
        </div>
      </div>

      {/* Associated apartments */}
      <div className="flex items-center justify-between mb-3" data-id="element-2324">
        <h2 className="text-sm font-semibold text-navy flex items-center gap-2" data-id="element-2325">
          <Home size={16} className="text-coral" data-id="element-2326" />
          Appartamenti associati
        </h2>
        <span className="text-xs text-muted-foreground" data-id="element-2327">
          {apartments.length}{' '}
          {apartments.length === 1 ? 'immobile' : 'immobili'}
        </span>
      </div>

      {apartments.length > 0 ? <div className="space-y-3" data-id="element-2328">
          {apartments.map(a => {
        const remaining = a.cleaningsTotal - a.cleaningsDone;
        return <div key={a.id} role="button" tabIndex={0} onClick={() => navigate(`/cm/clienti/appartamenti/${a.id}`)} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(`/cm/clienti/appartamenti/${a.id}`);
          }
        }} aria-label={`Apri appartamento ${a.name}`} className="w-full cursor-pointer text-left bg-white border border-border rounded-xl shadow-sm p-3 flex items-center gap-3 hover:border-coral/50 hover:shadow transition-all focus:outline-none focus:ring-2 focus:ring-coral" data-id="element-2329">
                <img src={a.cover} alt={a.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0 border border-border bg-gray-100" data-id="element-2330" />
                <div className="flex-1 min-w-0" data-id="element-2331">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-2332">
                    <span className="text-sm font-semibold text-navy truncate" data-id="element-2333">
                      {a.name}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap ${APT_TYPE_STYLE[a.type]}`} data-id="element-2334">
                      {a.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5" data-id="element-2335">
                    <MapPin size={11} className="text-gray-400 flex-shrink-0" data-id="element-2336" />
                    <span className="truncate" data-id="element-2337">{a.address}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px]" data-id="element-2338">
                    {a.contract?.active ? <button type="button" onClick={e => {
                e.stopPropagation();
                navigate(`/cm/clienti/appartamenti/${a.id}/contratto`);
              }} aria-label={`Apri dettaglio contratto di ${a.name}`} className="inline-flex items-center gap-1 text-emerald-600 font-medium hover:text-emerald-700 hover:underline focus:outline-none focus:ring-2 focus:ring-emerald-300 rounded" data-id="element-2339">
                        <CheckCircle2 size={11} data-id="element-2340" />
                        Contratto attivo
                        <ChevronRight size={11} data-id="element-2341" />
                      </button> : <span className="text-gray-400" data-id="element-2342">Nessun contratto</span>}
                    {a.cleaningsTotal > 0 && <span className="text-gray-500" data-id="element-2343">
                        {a.cleaningsDone}/{a.cleaningsTotal} pulizie ·{' '}
                        {remaining} rimaste
                      </span>}
                  </div>
                </div>
                <ChevronRight size={18} className="text-gray-300 flex-shrink-0" data-id="element-2344" />
              </div>;
      })}
        </div> : <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-2345">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-2346">
            <Building2 size={20} className="text-muted-foreground" data-id="element-2347" />
          </div>
          <p className="text-sm font-medium text-foreground" data-id="element-2348">
            Nessun appartamento associato
          </p>
          <p className="text-xs text-muted-foreground mt-1" data-id="element-2349">
            Gli immobili gestiti da questo referente compariranno qui.
          </p>
        </div>}
    </MobileLayout>;
}